import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Clock, RefreshCw, CheckCircle2, XCircle, Ban, PackageSearch, MapPin, CreditCard } from "lucide-react";
import { ordersAPI } from "../../api";
import { useAuth } from "../../context/AuthContext";
import PaymentModal from "../PaymentModal";
import ActionsList from "../ActionsList";
import LocationDisplay from "../Location/LocationDisplay";
import "./Orders.css";

const OrdersList = () => {
  const { isAuthenticated, isBreeder, isRestaurant, user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const response = await ordersAPI.getAll(params);
      setOrders(response.data);
    } catch (err) {
      setError("Erreur lors du chargement des commandes");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    if (isAuthenticated) {
      loadOrders();
    }
  }, [isAuthenticated, loadOrders]);

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: "En attente", class: "badge-warning" },
      confirmed: { label: "Confirmée", class: "badge-info" },
      preparing: { label: "En préparation", class: "badge-info" },
      ready: { label: "Prête", class: "badge-success" },
      shipped: { label: "Expédiée", class: "badge-primary" },
      delivered: { label: "Livrée", class: "badge-success" },
      cancelled: { label: "Annulée", class: "badge-error" },
    };
    const statusInfo = statusMap[status] || { label: status, class: "badge-primary" };
    return <span className={`badge ${statusInfo.class}`}>{statusInfo.label}</span>;
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    if (!paymentStatus) return null;
    const statusMap = {
      pending: { label: "Non payé", class: "badge-warning", Icon: Clock },
      processing: { label: "En cours", class: "badge-info", Icon: RefreshCw },
      success: { label: "Payé", class: "badge-success", Icon: CheckCircle2 },
      failed: { label: "Échoué", class: "badge-error", Icon: XCircle },
      cancelled: { label: "Annulé", class: "badge-error", Icon: Ban },
    };
    const statusInfo = statusMap[paymentStatus] || { label: paymentStatus, class: "badge-primary", Icon: null };
    const StatusIcon = statusInfo.Icon;
    return (
      <span className={`badge ${statusInfo.class} icon-inline`} style={{ fontSize: "0.75rem" }}>
        {StatusIcon && <StatusIcon size={12} strokeWidth={2.25} />} {statusInfo.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="container main-content">
        <div className="alert alert-error">
          Vous devez être connecté pour voir vos commandes
        </div>
        <Link to="/login" className="btn btn-primary">
          Se connecter
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container main-content">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container main-content">
      <ActionsList />
      
      <div className="page-header">
        <h1>{isBreeder ? "Commandes reçues" : "Mes commandes"}</h1>
        <p>{isBreeder ? "Commandes passées par les restaurateurs" : "Suivez l'état de vos commandes"}</p>
      </div>

      <div className="filters-section">
        <div className="form-group">
          <label htmlFor="status" className="form-label">Filtrer par statut</label>
          <select
            id="status"
            className="form-control"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ maxWidth: "300px" }}
          >
            <option value="">Tous les statuts</option>
            <option value="pending">En attente</option>
            <option value="confirmed">Confirmée</option>
            <option value="preparing">En préparation</option>
            <option value="ready">Prête</option>
            <option value="shipped">Expédiée</option>
            <option value="delivered">Livrée</option>
            <option value="cancelled">Annulée</option>
          </select>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {orders.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><PackageSearch size={56} strokeWidth={1.75} /></div>
          <h3>Aucune commande</h3>
          <p>
            {isBreeder 
              ? "Vous n'avez pas encore reçu de commande" 
              : "Vous n'avez pas encore passé de commande"}
          </p>
          {isRestaurant && (
            <Link to="/products" className="btn btn-primary">
              Voir les produits
            </Link>
          )}
        </div>
      ) : (
        <div className="orders-list" data-tour="orders-list">
          {orders.map((order) => (
            <div key={order.id} className="order-card card">
              <div className="order-header">
                <div>
                  <h3>Commande #{order.id}</h3>
                  <p className="order-date">
                    Passée le {formatDate(order.created_at)}
                  </p>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", alignItems: "flex-end" }}>
                  {getStatusBadge(order.status)}
                  {getPaymentStatusBadge(order.payment_status)}
                </div>
              </div>

              <div className="order-body">
                <div className="order-product">
                  <Link
                    to={`/products/${order.product.id}`}
                    className="order-product-link"
                  >
                    <strong>{order.product.name}</strong>
                  </Link>
                  <p>
                    Quantité: {order.quantity} {order.product.unit}
                  </p>
                  <p>
                    Prix unitaire: {order.product.unit === "lot" && order.product.lot_price
                      ? `${order.product.lot_price.toLocaleString("fr-FR")} F CFA / lot`
                      : `${order.product.price.toLocaleString("fr-FR")} F CFA / ${order.product.unit}`}
                  </p>
                </div>

                <div className="order-parties">
                  {isBreeder ? (
                    <div>
                      <strong>Client:</strong> {order.buyer.name}
                      {order.buyer.city && (
                        <span> - {order.buyer.city}</span>
                      )}
                    </div>
                  ) : (
                    <>
                      <div>
                        <strong>Vendeur:</strong> {order.seller.name}
                        {order.seller.city && (
                          <span> - {order.seller.city}</span>
                        )}
                      </div>
                      <div>
                        <strong>Acheteur:</strong> {order.buyer.name}
                      </div>
                    </>
                  )}
                </div>

                {order.delivery_address && (
                  <div className="order-delivery" style={{
                    padding: "0.75rem",
                    background: (order.delivery_type === "breeder" || order.delivery_type === "third_party") && (order.status === "ready" || order.status === "shipped" || order.status === "preparing")
                      ? "rgba(255, 193, 7, 0.1)"
                      : "transparent",
                    borderRadius: "var(--radius-sm)",
                    border: (order.delivery_type === "breeder" || order.delivery_type === "third_party") && (order.status === "ready" || order.status === "shipped" || order.status === "preparing")
                      ? "1px solid #ffc107"
                      : "none"
                  }}>
                    <strong className="icon-inline">
                      <MapPin size={14} strokeWidth={2.25} />
                      {(order.delivery_type === "breeder" || order.delivery_type === "third_party") && (order.status === "ready" || order.status === "shipped" || order.status === "preparing")
                        ? "Adresse de livraison (à livrer):"
                        : "Adresse de livraison:"}
                    </strong>
                    <LocationDisplay address={order.delivery_address} />
                  </div>
                )}
                {/* Adresse de l'éleveur pour livraison par le restaurant */}
                {order.delivery_type === "restaurant" && order.seller && order.seller.address && (
                  <div className="order-delivery" style={{
                    padding: "0.75rem",
                    background: (order.status === "ready" || order.status === "shipped" || order.status === "preparing")
                      ? "rgba(33, 150, 243, 0.1)"
                      : "transparent",
                    borderRadius: "var(--radius-sm)",
                    border: (order.status === "ready" || order.status === "shipped" || order.status === "preparing")
                      ? "1px solid #2196F3"
                      : "none"
                  }}>
                    <strong className="icon-inline">
                      <MapPin size={14} strokeWidth={2.25} />
                      {(order.status === "ready" || order.status === "shipped" || order.status === "preparing")
                        ? "Adresse de l'éleveur (à récupérer):"
                        : "Adresse de l'éleveur:"}
                    </strong>
                    <LocationDisplay address={order.seller.address} />
                  </div>
                )}
                {/* Point de rencontre */}
                {order.meeting_point_address && (
                  <div className="order-delivery" style={{
                    padding: "0.75rem",
                    background: order.delivery_type === "meeting_point" && (order.status === "ready" || order.status === "shipped" || order.status === "preparing")
                      ? "rgba(33, 150, 243, 0.1)"
                      : "transparent",
                    borderRadius: "var(--radius-sm)",
                    border: order.delivery_type === "meeting_point" && (order.status === "ready" || order.status === "shipped" || order.status === "preparing")
                      ? "1px solid #2196F3"
                      : "none"
                  }}>
                    <strong className="icon-inline">
                      <MapPin size={14} strokeWidth={2.25} />
                      {order.delivery_type === "meeting_point" && (order.status === "ready" || order.status === "shipped" || order.status === "preparing")
                        ? "Point de rencontre (à récupérer):"
                        : "Point de rencontre:"}
                    </strong>
                    <LocationDisplay address={order.meeting_point_address} />
                  </div>
                )}

                {order.notes && (
                  <div className="order-notes">
                    <strong>Notes:</strong> {order.notes}
                  </div>
                )}
              </div>

              <div className="order-footer">
                <div className="order-total">
                  <strong>Total: {order.total_price.toLocaleString("fr-FR")} F CFA</strong>
                  {isRestaurant && order.buyer_id === user?.id && order.payment_status !== "success" && (
                    <div style={{ marginTop: "0.5rem", fontSize: "0.85rem" }}>
                      {getPaymentStatusBadge(order.payment_status)}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                  {isRestaurant && order.buyer_id === user?.id && order.payment_status !== "success" && order.payment_status !== "processing" && (
                    <button
                      onClick={() => {
                        setSelectedOrder(order);
                        setShowPaymentModal(true);
                      }}
                      className="btn btn-primary btn-sm"
                    >
<CreditCard size={14} strokeWidth={2.25} /> Payer
                    </button>
                  )}
                  <Link
                    to={`/orders/${order.id}`}
                    className="btn btn-outline btn-sm"
                  >
                    Voir les détails
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de paiement */}
      {selectedOrder && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedOrder(null);
          }}
          order={selectedOrder}
          onPaymentSuccess={() => {
            setShowPaymentModal(false);
            setSelectedOrder(null);
            loadOrders();
          }}
        />
      )}
    </div>
  );
};

export default OrdersList;
